import React, {useEffect, useState} from 'react'

import {useTranslation} from 'react-i18next'
import {Autocomplete, Box, FormControl, MenuItem, Modal, Select, TextField, Tooltip, Stack, RadioGroup, FormControlLabel, Radio} from '@mui/material'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import {useNavigate} from "react-router-dom";
import MenuBookIcon from '@mui/icons-material/MenuBook'

import style from '../../../styles/style'
import {useStateContext} from '../../../context/ContextProvider'
import axios from '../../../lib/axios'
import TableShared from '../../../components/TableShared'
import AddButton from '../../../components/AddButton'
import AddSalesOrderItem from '../../../components/sales_purchase_orders/AddSalesOrderItem'
import AddPurchaseOrderItem from '../../../components/sales_purchase_orders/AddPurchaseOrderItem';
import { canViewPrice, createRegexFromString, deleteZero, handleAxiosError, handleInteger, handleNumber, handleZero, isValid } from '../../../helpers/helper'
import { includes, toNumber } from 'lodash';
import { AUTOCOMPLETE_STYLE, MOBILE_DATE_PICKER_STYLE, MOBILE_TIME_PICKER_STYLE, SELECT_STYLE, TEXTFIELD_STYLE } from '../../../constants/constants';
import { useAuth } from '../../../hooks/auth';
import { t } from 'i18next'
import clsx from 'clsx'

const CreateTemplate = ({ open, handleOpen, setIsLoading }) => {
    const { user } = useAuth({ middleware: 'guest' })
    const { company_id, config, currencySymbol, features } = useStateContext()
    const navigate = useNavigate()

    /* state */
    const [customers, setCustomers] = useState([])
    const [vendors, setVendors] = useState([])
    const [shipToLocations, setShipToLocation] = useState([])
    const [purchaseLocations, setPurchaseLocations] = useState([])
    const [salesOrderItems, setSalesOrderItems] = useState([])
    const [purchaseOrderItems, setPurchaseOrderItems] = useState([])
    const [openSalesOrderItem, setOpenSalesOrderItem] = useState(false)
    const [openPurchaseOrderItem, setOpenPurchaseOrderItem] = useState(false)

    const [disabled, setDisabled] = useState(true)
    const [totalPrice, setTotalPrice] = useState(0)
    const [totalQty, setTotalQty] = useState(0)
    const [totalPricePurchase, setTotalPricePurchase] = useState(0)
    const [totalQtyPurchase, setTotalQtyPurchase] = useState(0)
    const [disabledButtonSales, setDisabledButtonSales] = useState(true)
    const [disabledButtonPurchase, setDisabledButtonPurchase] = useState(true)

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [vendorId, setVendor] = useState('')
    const [shipToAddressId, setShipToAddressId] = useState('')
    const [purchaseAddressId, setPurchaseAddressId] = useState('')

    const [address, setAddress] = useState('')
    const [zip, setZip] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [addressVendor, setAddressVendor] = useState('')
    const [zipVendor, setZipVendor] = useState('')
    const [cityVendor, setCityVendor] = useState('')
    const [stateVendor, setStateVendor] = useState('')

    const [SCItems, setSCItems] = useState([])
    const [PCItems, setPCItems] = useState([])

    const [type, setType] = useState('shipment')
    const [templateName, setTemplateName] = useState('')

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '13') {
                if (open) createTemplate()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [
        open,
        customerId,
        vendorId,
        shipToAddressId,
        purchaseAddressId,
        salesOrderItems,
        purchaseOrderItems
    ])

    /* UseEffect */
    useEffect(() => {
        if (open) {
            if (isValid(company_id)) {
                getCustomers(company_id)
                getVendors(company_id)
            }
        }
    }, [company_id, open])

    useEffect(() => {
        if (customerId) {
            getShipToLocations(customerId)
        }
        setSalesOrderItems([])
    }, [customerId])

    useEffect(() => {
        if (vendorId) {
            getPurchaseLocations(vendorId)
        }
        setPurchaseOrderItems([])
    }, [vendorId])

    useEffect(() => {
        setSalesOrderItems([])
        if (shipToLocations.length > 0 && customerId && shipToAddressId) {
            getSalesContractItems()
        }
    }, [shipToAddressId])

    useEffect(() => {
        setPurchaseOrderItems([])
        if (purchaseLocations.length > 0 && vendorId && purchaseAddressId) {
            getPurchaseContractItems()
        }
    }, [purchaseAddressId])


    useEffect(() => {
        if (SCItems.length > 0) {
            handleNewItem(SCItems)
        }
    }, [SCItems])

    useEffect(() => {
        if (PCItems.length > 0) {
            handleNewItemPurchase(PCItems)
        }
    }, [PCItems])

    useEffect(() => {
        handleTotalPrice()
        handleTotalQty()
    }, [salesOrderItems])

    useEffect(() => {
        handleTotalPricePurchase()
        handleTotalQtyPurchase()
    }, [purchaseOrderItems])

    /* Handlers */


    const handleNewItem = (e) => {
        e.map((item) => {
            let new_item = {
                "id": item.id,
                "item_no": item.item_no,
                "description": item.description,
                "qty_ordered": item.quantity,
                "unit_price": item.unit_price,
                "total_cost": item.quantity * item.unit_price,
                "item_id": item.item_id,
                "unit_of_measure_code": item.unit_of_measure_code,
                "unit_type": item?.unit_type
            }

            setSalesOrderItems((prev) => [...prev, new_item])
        })
    }

    const handleNewItemPurchase = (e) => {
        e.map((item) => {
            let new_item = {
                "id": item.id,
                "item_no": item.item_no,
                "description": item.description,
                "qty_ordered": item.quantity,
                "unit_price": item.unit_price,
                "total_cost": item.quantity * item.unit_price,
                "item_id": item.item_id,
                "unit_of_measure_code": item.unit_of_measure_code,
                "unit_type": item?.unit_type
            }

            setPurchaseOrderItems((prev) => [...prev, new_item])
        })
    }

    const handleTotalPrice = () => {
        const sum = salesOrderItems.reduce((accumulator, currentValue) => toNumber(accumulator) + toNumber(currentValue.total_cost), 0);
        setTotalPrice(sum);
    }

    const handleTotalQty = () => {
        const sum = salesOrderItems.reduce((sum, item) => sum + (item.qty_ordered || 0), 0);
        setTotalQty(sum);
    }

    const handleTotalPricePurchase = () => {
        const sum = purchaseOrderItems.reduce((accumulator, currentValue) => toNumber(accumulator) + toNumber(currentValue.total_cost), 0);
        setTotalPricePurchase(sum);
    }

    const handleTotalQtyPurchase = () => {
        const sum = purchaseOrderItems.reduce((sum, item) => sum + (item.qty_ordered || 0), 0);
        setTotalQtyPurchase(sum);
    }

    const handleToggleModal = () => {
        handleOpen()
        setTemplateName('')
        setCustomerId('')
        setVendor('')
        setShipToAddressId('')
        setPurchaseAddressId('')
        setSalesOrderItems([])
        setPurchaseOrderItems([])
        setDisabledButtonSales(true)
        setDisabledButtonPurchase(true)
    }

    const handleShipToLocation = (e) => {
        setShipToAddressId(e.target.value)
        const item = shipToLocations.find(item => item.id === e.target.value)
        setAddress(item.address)
        setZip(item.zip)
        setCity(item.city)
        setState(item.state)
    }

    const handlePurchaseLocation = (e) => {
        setPurchaseAddressId(e.target.value)
        const item = purchaseLocations.find(item => item.id === e.target.value)
        setAddressVendor(item.address)
        setZipVendor(item.zip)
        setCityVendor(item.city)
        setStateVendor(item.state)
    }

    const handleUpdateItem = (id, quantity, unit_price) => {
        const salesOrderItemsCopy = [...salesOrderItems]
        const item = salesOrderItemsCopy.find(item => item.id === id)

        item.qty_ordered = toNumber(quantity)
        item.unit_price = unit_price
        item.total_cost = toNumber(quantity) * unit_price

        setSalesOrderItems(salesOrderItemsCopy)
    }

    const handleUpdateItemPurchase = (id, quantity, unit_price) => {
        const purchaseOrderItemsCopy = [...purchaseOrderItems]
        const item = purchaseOrderItemsCopy.find(item => item.id === id)

        item.qty_ordered = toNumber(quantity)
        item.unit_price = unit_price
        item.total_cost = toNumber(quantity) * unit_price

        setPurchaseOrderItems(purchaseOrderItemsCopy)
    }

    const handleOpenSalesOrderItem = () => {
        setDisabled(true)
        setOpenSalesOrderItem(!openSalesOrderItem)
    }

    const handleOpenPurchaseOrderItem = () => {
        setDisabled(true)
        setOpenPurchaseOrderItem(!openPurchaseOrderItem)
    }


    /* API Requests */

    const getSalesContractItems = async () => {
        setIsLoading(true)
        await axios.get(`/api/sales-contracts?customer_id=${customerId}&ship_address_id=${shipToAddressId}`, config)
            .then(res => {
                const items = res.data
                setSCItems(items[0]?.sales_contract_items || [])
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getPurchaseContractItems = async () => {
        setIsLoading(true)
        await axios
            .get(`/api/purchase-contracts?vendor_id=${vendorId}&purchase_address_id=${purchaseAddressId}`, config)
            .then(res => {
                const items = res.data
                setPCItems(items[0]?.purchase_contract_items || [])
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getCustomers = async (id) => {
        setCustomerId('')

        setIsLoading(true)
        await axios
            .get(`/api/customers?company_id=${id}&blocked=0`, config)
            .then(res => {
                const customers = res.data
                setCustomers(customers)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getVendors = async (id) => {
        setVendor('')

        setIsLoading(true)
        await axios
            .get(`/api/vendors?company_id=${id}&blocked=0`, config)
            .then(res => {
                const vendors = res.data
                setVendors(vendors)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getShipToLocations = async (id) => {
        setShipToAddressId('')
        setAddress('')
        setZip('')
        setCity('')
        setState('')

        setIsLoading(true)
        await axios.get(`/api/ship-addresses?customer_id=${id}`, config)
            .then(res => {
                const data = res.data
                setShipToLocation(data)
                setShipToAddressId(data[0]?.id || '')
                setAddress(data[0]?.address || '')
                setZip(data[0]?.zip || '')
                setCity(data[0]?.city || '')
                setState(data[0]?.state || '')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getPurchaseLocations = async (id) => {
        setPurchaseAddressId('')
        setAddressVendor('')
        setZipVendor('')
        setCityVendor('')
        setStateVendor('')

        setIsLoading(true)
        await axios
            .get(`/api/purchase-addresses?vendor_id=${id}`, config)
            .then(res => {
                const data = res.data
                setPurchaseLocations(data)
                setPurchaseAddressId(data[0]?.id || '')
                setAddressVendor(data[0]?.address || '')
                setZipVendor(data[0]?.zip || '')
                setCityVendor(data[0]?.city || '')
                setStateVendor(data[0]?.state || '')
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const createTemplate = async () => {
        const mustMatch = ['1', 'true', true, 1].includes(features?.TRANSACTION_QUANTITIES_MUST_MATCH);
        if(mustMatch) {
            const ignoreOthers = ['1', 'true', true, 1].includes(features?.IGNORE_OTHER_ITEMS);

            let salesOrderItemsCopy = salesOrderItems;
            let purchaseOrderItemsCopy = purchaseOrderItems;

            if(ignoreOthers) {
                salesOrderItemsCopy = salesOrderItemsCopy.filter((item) => item?.unit_type !== 'other');
                purchaseOrderItemsCopy = purchaseOrderItemsCopy.filter((item) => item?.unit_type !== 'other');
            }

            const salesOrdersSum = salesOrderItemsCopy.reduce((accumulator, current) => accumulator + current.qty_ordered, 0)
            const purchaseOrdersSum = purchaseOrderItemsCopy.reduce((accumulator, current) => accumulator + current.qty_ordered, 0)
            if(salesOrdersSum !== purchaseOrdersSum) {
                Swal.fire({
                    text: t('ordered_quantities_not_equal_warning__alt'),
                    icon: "error",
                    customClass: 'error',
                    showCloseButton: true,
                    iconColor: '#FF0000'
                })

                return
            }
        }

        setIsLoading(true)
        const formData = {}

        formData['name'] = templateName
        formData['type'] = type
        formData['customer_id'] = customerId
        formData['vendor_id'] = vendorId
        formData['ship_address_id'] = shipToAddressId
        formData['purchase_address_id'] = purchaseAddressId
        formData['sales_order_items'] = salesOrderItems
        formData['purchase_order_items'] = purchaseOrderItems
        formData['company_id'] = company_id

        await axios
            .post(`/api/templates`, formData, config)
            .then(({ data }) => {
                Swal.fire({
                    icon: "success",
                    customClass: 'success',
                    showCloseButton: true,
                    iconColor: '#00B78E',
                    text: data.success.message
                })

                navigate("/templates/" + data.success.data.id)

                handleToggleModal()
            })
            .catch(({ response }) => {
                if(response?.status === 422) {
                    handleAxiosError({response: response, showDescription: true})
                } else {
                    handleAxiosError({response: response})
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getSalesColumns = () => {
        const columns = [];

        columns.push(
            {
                field: 'item_no',
                headerName: t('no'),
                flex: 0.7,
                minWidth: 75
            },
            {
                field: 'description',
                headerName: t('description'),
                flex: 1.5,
                maxWidth: 140
            },
            {
                field: 'unit_of_measure_code',
                headerName: t('uom'),
                flex: 0.7,
                minWidth: 75
            },
            {
                field: 'qty_ordered',
                headerName: (type === 'collection' && ['arbor-trace', 'qa'].includes(localStorage.getItem('client_id'))) ? t('qty_expected') : t('qty_ordered'),
                flex: 1,
                minWidth: 100,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => <QtyOrderedSalesCell params={params} handler={handleUpdateItem} />
            }
        );

        if(canViewPrice(user, features)) {
            columns.push(
                {
                    field: 'unit_price',
                    headerName: t('unit_price'),
                    flex: 0.7,
                    minWidth: 75,
                    renderCell: (params) => <UnitPriceSalesCell params={params} currencySymbol={currencySymbol} />
                },
                {
                    field: 'total_cost',
                    headerName: t('total'),
                    flex: 0.7,
                    minWidth: 75,
                    renderCell: (params) => <TotalSalesCell params={params} currencySymbol={currencySymbol} />
                }
            );
        }

        columns.push(
            {
                field: 'actions',
                headerName: t('actions'),
                sortable: false,
                flex: 0.5,
                minWidth: 50,
                cellClassName: 'padding-0',
                renderCell: (params) => <Actions params={params} t={t} salesOrderItems={salesOrderItems} setSalesOrderItems={setSalesOrderItems} />
            }
        );

        return columns;
    }

    const getPurchaseColumns = () => {
        const columns = [];

        columns.push(
            {
                field: 'item_no',
                headerName: t('no'),
                flex: 0.7,
                minWidth: 75
            },
            {
                field: 'description',
                headerName: t('description'),
                flex: 1.5,
                maxWidth: 140
            },
            {
                field: 'unit_of_measure_code',
                headerName: t('uom'),
                flex: 0.7,
                minWidth: 75
            },
            {
                field: 'qty_ordered',
                headerName: (type === 'collection' && ['arbor-trace', 'qa'].includes(localStorage.getItem('client_id'))) ? t('qty_expected') : t('qty_ordered'),
                align: 'center',
                headerAlign: 'center',
                flex: 1,
                minWidth: 100,
                renderCell: (params) => <QtyOrderedPurchaseCell params={params} handler={handleUpdateItemPurchase} />
            }
        );

        if(canViewPrice(user, features)) {
            columns.push(
                {
                    field: 'unit_price',
                    headerName: t('unit_price'),
                    flex: 0.7,
                    minWidth: 75,
                    renderCell: (params) => <UnitPricePurchaseCell params={params} currencySymbol={currencySymbol} />
                },
                {
                    field: 'total_cost',
                    headerName: t('total'),
                    flex: 0.7,
                    minWidth: 75,
                    renderCell: (params) => <TotalPurchaseCell params={params} currencySymbol={currencySymbol} />
                }
            );
        }

        columns.push(
            {
                field: 'actions',
                headerName: t('actions'),
                sortable: false,
                flex: 0.5,
                minWidth: 50,
                cellClassName: 'padding-0',
                renderCell: (params) => <ActionsPurchase params={params} t={t} purchaseOrderItems={purchaseOrderItems} setPurchaseOrderItems={setPurchaseOrderItems} />
            }
        );

        return columns;
    }

    const SalesTotalPrice = () => {
        if(canViewPrice(user, features)) {
            return (
                <div className='flex justify-between text-[#333333] text-[15px] pb-4'>
                    <p>{t('totalPrice')}</p>
                    <p className='font-[500]'>{currencySymbol}{totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
            );
        }

        return;
    }

    const PurchaseTotalPrice = () => {
        if(canViewPrice(user, features)) {
            return (
                <div className='flex justify-between text-[#333333] text-[15px] pb-4'>
                    <p>{t('totalPrice')}</p>
                    <p className='font-[500]'>{currencySymbol}{totalPricePurchase.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
            );
        }

        return;
    }

    const customerOptions = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))
    const vendorsOptions = vendors?.map(option => ({ label: option.vendor_no + ' - ' + option.name, id: option.id }))

    return (
        <>
            <Modal open={open} onClose={handleToggleModal}>
                <Box sx={style}>
                    <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-center justify-between w-full leading-none'>
                            <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#148c8cb3] text-[35px] mr-4'>•</span> {t('create_new_x', {x: t('template')})}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                                <button onClick={handleToggleModal}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>
                    <div className='p-5 pb-12 flex justify-between gap-3 flex-col lg:flex-row'>
                        <div className='w-full lg:w-2/5'>
                            {/* TEMPLATE INFORMATION SECTION */}
                            <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                                <div className='pb-5 pt-1'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('template_information')} *</p>
                                </div>
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('template_name')} *</p>
                                    <div className='w-full'>
                                        <TextField
                                            sx={TEXTFIELD_STYLE}
                                            type="text"
                                            name="template_name"
                                            value={templateName}
                                            onChange={(e) => { setTemplateName(e.target.value) }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* TYPE SECTION */}
                            <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                                <div className='pb-5 pt-1'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('order_type')} *</p>
                                </div>
                                <FormControl className='w-full pb-4'>
                                    <RadioGroup
                                        name="template-type-radio-button-group"
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                        row
                                    >
                                        <FormControlLabel value="shipment" control={<Radio />} label={t('shipment')} />
                                        <FormControlLabel value="collection" control={<Radio />} label={t('collection')} />
                                    </RadioGroup>
                                </FormControl>
                            </div>

                            <div
                                className={clsx(
                                    'flex flex-col', {
                                        'flex-col-reverse': type === 'collection'
                                    }
                                )}
                            >
                                {/* CUSTOMER section */}
                                <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                                    <div className='pb-5 pt-1'>
                                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{type === 'collection' ? t('service_provider') : t('customer')}</p>
                                    </div>
                                    {/* Customer */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{type === 'collection' ? t('service_provider') : t('customer')} *</p>
                                        <Autocomplete
                                            disablePortal
                                            disableClearable
                                            id="customer-autocomplete"
                                            options={customerOptions}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            sx={{
                                                ...AUTOCOMPLETE_STYLE,
                                                '.MuiOutlinedInput-input': {
                                                    padding: '0 !important'
                                                }
                                            }}
                                            onChange={(a, b) => { setCustomerId(b?.id || ''); setDisabledButtonSales(false) }}
                                            renderInput={(params) => <TextField {...params} autoFocus={true} />}
                                        />
                                    </div>
                                    {/* Ship to code */}
                                    <>
                                        <div className='w-full pb-4'>
                                            <p className="text-[14px] text-[#718096]">{t('ship_to_location')} *</p>
                                            <div className='w-full'>
                                                <FormControl sx={{ width: '100%' }}>
                                                    <Select
                                                        value={shipToAddressId}
                                                        onChange={(e) => { handleShipToLocation(e) }}
                                                        sx={SELECT_STYLE}
                                                    >
                                                        {shipToLocations?.map((item, index) => <MenuItem key={index} value={item.id}>{item.code} - {item.name}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        </div>
                                        {/* Address */}
                                        {
                                            shipToAddressId ?
                                                <div className='w-full p-4 address mb-4'>
                                                    <div className='w-full'>{address}</div>
                                                    {shipToAddressId ? <div className='w-full'>{zip} {city}, {state}</div> : <div className='w-full'></div>}
                                                </div>
                                                :
                                                null
                                        }
                                    </>
                                </div>

                                {/* SUPPLIER section */}
                                <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                                    <div className='pb-5 pt-1'>
                                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('supplier')}</p>
                                    </div>
                                    {/* Vendor */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('supplier')} *</p>
                                        <div className='w-full'>
                                            <Autocomplete
                                                disablePortal
                                                disableClearable
                                                id="vendor-autocomplete"
                                                options={vendorsOptions}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                sx={{
                                                    ...AUTOCOMPLETE_STYLE,
                                                    '.MuiOutlinedInput-input': {
                                                        padding: '0 !important'
                                                    }
                                                }}
                                                onChange={(a, b) => { setVendor(b?.id || ''); setDisabledButtonPurchase(false) }}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </div>
                                    </div>

                                    {/* Purchase Location */}
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('purchaseLocation')} *</p>
                                        <div className='w-full'>
                                            <FormControl sx={{ width: '100%' }}>
                                                <Select
                                                    value={purchaseAddressId}
                                                    onChange={(e) => { handlePurchaseLocation(e) }}
                                                    sx={SELECT_STYLE}
                                                >
                                                    {purchaseLocations?.map((item, index) => <MenuItem key={index} value={item.id}>{item.code} - {item.name}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>

                                    {/* Address */}
                                    {
                                        purchaseAddressId && (
                                            <div className='w-full p-4 address mb-4'>
                                                <div className='w-full'>{addressVendor}</div>
                                                {purchaseAddressId ? <div className='w-full'>{zipVendor} {cityVendor}, {stateVendor}</div> : <div className='w-full'></div>}
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='w-full lg:w-3/5'>
                            <div
                                className={clsx(
                                    'flex flex-col', {
                                        'flex-col-reverse': type === 'collection'
                                    }
                                )}
                            >
                                {/* Sales Order Items */}
                                <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                                    <div className='pt-1 pl-3'>
                                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('salesOrderitems')}</p>
                                    </div>
                                    <div className='flex justify-end p-5 pt-0 border-b'>
                                        <Tooltip disableInteractive title={t('addsalesOrderitems')} placement='bottom'>
                                            <div>
                                                <AddButton disabled={disabledButtonSales} onClick={handleOpenSalesOrderItem}><i className="fa-solid fa-plus"></i></AddButton>
                                            </div>
                                        </Tooltip>
                                    </div>
                                    <div className='border-b'>
                                        {
                                            salesOrderItems.length > 0 ?
                                                <div className='flex justify-end w-full p-2'>
                                                    <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={e => setSalesOrderItems([])}>{t('clear_all')}</button>
                                                </div>
                                                :
                                                null
                                        }
                                        <TableShared columns={getSalesColumns()} items={salesOrderItems} disableColumnMenu={true} />
                                    </div>
                                    <div className='flex justify-end w-full p-5'>
                                        <div className='w-full lg:w-1/3'>
                                            <SalesTotalPrice />
                                            <div className='flex justify-between text-[#333333] text-[15px]'>
                                                <p>{t('totalQty')}</p>
                                                <p className='font-[500]'>{totalQty}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Purchase Order Items */}
                                <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                                    <div className='pt-1 pl-3'>
                                        <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('purchaseOrderItems')}</p>
                                    </div>
                                    <div className='flex justify-end p-5 pt-0 border-b'>
                                        <Tooltip disableInteractive title={t('addpurchaseOrderItems')} placement='bottom'>
                                            <div>
                                                <AddButton disabled={disabledButtonPurchase} onClick={handleOpenPurchaseOrderItem}><i className="fa-solid fa-plus"></i></AddButton>
                                            </div>
                                        </Tooltip>
                                    </div>
                                    <div className='border-b'>
                                        {
                                            purchaseOrderItems.length > 0 ?
                                                <div className='flex justify-end w-full p-2'>
                                                    <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={e => setPurchaseOrderItems([])}>{t('clear_all')}</button>
                                                </div>
                                                :
                                                null
                                        }
                                        <TableShared columns={getPurchaseColumns()} items={purchaseOrderItems} disableColumnMenu={true} />
                                    </div>

                                    <div className='flex justify-end w-full p-5'>
                                        <div className='w-full lg:w-1/3'>
                                            <PurchaseTotalPrice />
                                            <div className='flex justify-between text-[#333333] text-[15px]'>
                                                <p>{t('totalQty')}</p>
                                                <p className='font-[500]'>{totalQtyPurchase}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='w-full flex justify-end'>
                                <button type="button" onClick={createTemplate} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                                    <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                                </button>
                            </div>
                        </div>
                    </div>
                </Box>
            </Modal >
            <AddSalesOrderItem open={openSalesOrderItem} handleOpen={handleOpenSalesOrderItem} salesOrderItems={salesOrderItems} setSalesOrderItems={setSalesOrderItems} disabled={disabled} setDisabled={setDisabled} purchaseOrderItems={purchaseOrderItems} setPurchaseOrderItems={setPurchaseOrderItems} />
            <AddPurchaseOrderItem open={openPurchaseOrderItem} handleOpen={handleOpenPurchaseOrderItem} purchaseOrderItems={purchaseOrderItems} setPurchaseOrderItems={setPurchaseOrderItems} disabled={disabled} setDisabled={setDisabled} salesOrderItems={salesOrderItems} setSalesOrderItems={setSalesOrderItems} />
        </>
    )
}



export default CreateTemplate

const maxQtyDigits = 5

{/* SALES ORDER ITEM cells */}

const QtyOrderedSalesCell = ({ params, handler }) => {
    const [qty, setQty] = useState(params.value)

    useEffect(() => {
        setQty(params.value)
    }, [params.row.qty_ordered])

    const handleZeroLocal = (e) => {
        if (e.target.value === "" || e.target.value === '-') {
            setQty(0)

            handler(
                params.row.id,
                0,
                params.row.unit_price
            )
        }
    }

    const handleQuantity = (e) => {
        const regex = createRegexFromString(`/^(-?\\d{0,${maxQtyDigits}})$/i`)
        if (regex.test(e.target.value)) {
            if (e.target.value < 0 || e.target.value === '-') {
                setQty(0)
            } else {
                setQty(e.target.value)

                handler(
                    params.row.id,
                    e.target.value,
                    params.row.unit_price
                )
            }
        }
    }

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-2/3 mb-5 px-0 pt-0'
            name="qty-ordered"
            value={qty}
            onChange={(e) => handleQuantity(e) }
            onBlur={handleZeroLocal}
            onFocus={e => deleteZero({
                event: e,
                setter: setQty,
                value: qty
            })}
        />
    )
}

const UnitPriceSalesCell = ({ params, currencySymbol }) => {
    return (
        <>{currencySymbol}{params.row.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}

const TotalSalesCell = ({ params, currencySymbol }) => {
    const { value, row } = params;

    const [total, setTotal] = useState(value)

    useEffect(() => {
        setTotal(row.total_cost);
    }, [params])

    return (
        <>{currencySymbol}{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}

const Actions = ({ params, t, salesOrderItems, setSalesOrderItems }) => {
    const deleteItem = async (id) => {
        const filtered = salesOrderItems.filter(i => i.id != id)
        setSalesOrderItems(filtered)
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteItem(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}

{/* PURCHASE ORDER ITEM cells */}

const QtyOrderedPurchaseCell = ({ params, handler }) => {
    const [qty, setQty] = useState(params.value)

    useEffect(() => {
        setQty(params.value)
    }, [params.row.qty_ordered])

    const handleZeroLocal = (e) => {
        if (e.target.value === "") {
            setQty(0)

            handler(
                params.row.id,
                0,
                params.row.unit_price
            )
        }
    }

    const handleQuantity = (e) => {
        const regex = createRegexFromString(`/^(-?\\d{0,${maxQtyDigits}})$/i`)
        if (regex.test(e.target.value)) {
            if (e.target.value < 0 || e.target.value === '-') {
                setQty(0)
            } else {
                setQty(e.target.value)

                handler(
                    params.row.id,
                    e.target.value,
                    params.row.unit_price
                )
            }
        }
    }

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-2/3 mb-5 px-0 pt-0'
            name="qty-ordered-purchase"
            value={qty}
            onChange={(e) => handleQuantity(e) }
            onBlur={handleZeroLocal}
            onFocus={e => deleteZero({
                event: e,
                setter: setQty,
                value: qty
            })}
        />
    )
}

const UnitPricePurchaseCell = ({ params, currencySymbol }) => {
    return (
        <>{currencySymbol}{params.row.unit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}

const TotalPurchaseCell = ({ params, currencySymbol }) => {
    const { value, row } = params;

    const [total, setTotal] = useState(value)

    useEffect(() => {
        setTotal(row.total_cost);
    }, [params])

    return (
        <>{currencySymbol}{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    )
}

const ActionsPurchase = ({ params, t, purchaseOrderItems, setPurchaseOrderItems }) => {
    const deleteItem = async (id) => {
        const filtered = purchaseOrderItems.filter(i => i.id != id)
        setPurchaseOrderItems(filtered)
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteItem(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}
